import { useState, useCallback, useMemo, useRef, useEffect } from "react";

export interface VideoTelemetrySyncState {
  currentTime: number; // Current synchronized time in seconds
  isVideoControlled: boolean; // True if video is driving the sync, false if telemetry is
  selectedVideoId: string | null;
  isPlaying: boolean;
  source: "video" | "telemetry" | "keyboard" | null; // Source of the last update
  syncOffset: number; // Offset in seconds to align video and telemetry timestamps
}

interface VideoTelemetrySyncActions {
  setCurrentTime: (
    time: number,
    source: "video" | "telemetry" | "keyboard",
  ) => void;
  setSelectedVideoId: (videoId: string) => void;
  setIsPlaying: (playing: boolean) => void;
  syncToTime: (time: number) => void;
  setSyncOffset: (offset: number) => void;
  reset: () => void;
}

export function useVideoTelemetrySync(initialTime: number = 0) {
  const [state, setState] = useState<VideoTelemetrySyncState>({
    currentTime: initialTime,
    isVideoControlled: false,
    selectedVideoId: null,
    isPlaying: false,
    source: null,
    syncOffset: 0,
  });

  // Debounce rapid updates to prevent sync loops
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastUpdateSourceRef = useRef<"video" | "telemetry" | "keyboard" | null>(
    null,
  );
  const recentSyncOffsetChangeRef = useRef<number>(0);

  const setCurrentTime = useCallback(
    (time: number, source: "video" | "telemetry" | "keyboard") => {
      // Clear any pending updates
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // Use setState to access current state and prevent feedback loops
      setState((prevState) => {
        // Ignore video updates for a short time after sync offset changes
        if (
          source === "video" &&
          Date.now() - recentSyncOffsetChangeRef.current < 200
        ) {
          return prevState;
        }

        // Prevent feedback loops by checking the last update source
        if (
          lastUpdateSourceRef.current === source &&
          Math.abs(prevState.currentTime - time) < 0.1
        ) {
          return prevState; // No change needed
        }

        lastUpdateSourceRef.current = source;

        // Debounce updates to prevent rapid oscillation
        updateTimeoutRef.current = setTimeout(() => {
          setState((prev) => {
            // Additional check inside setState to prevent unnecessary updates
            if (
              Math.abs(prev.currentTime - time) < 0.1 &&
              prev.source === source
            ) {
              return prev;
            }
            return {
              ...prev,
              currentTime: time,
              isVideoControlled: source === "video",
              source: source,
            };
          });
          lastUpdateSourceRef.current = null;
        }, 50);

        return prevState; // Return current state unchanged for this call
      });
    },
    [], // No dependencies needed since we access state through setState
  );

  const setSelectedVideoId = useCallback((videoId: string) => {
    setState((prev) => ({
      ...prev,
      selectedVideoId: videoId,
    }));
  }, []);

  const setIsPlaying = useCallback((playing: boolean) => {
    setState((prev) => ({
      ...prev,
      isPlaying: playing,
    }));
  }, []);

  const syncToTime = useCallback((time: number) => {
    setState((prev) => ({
      ...prev,
      currentTime: time,
      isVideoControlled: false,
      source: null,
    }));
  }, []);

  const setSyncOffset = useCallback((offset: number) => {
    // Mark that we recently changed sync offset to prevent video updates from interfering
    recentSyncOffsetChangeRef.current = Date.now();
    setState((prev) => ({
      ...prev,
      syncOffset: offset,
    }));
  }, []);

  const reset = useCallback(() => {
    setState({
      currentTime: initialTime,
      isVideoControlled: false,
      selectedVideoId: null,
      isPlaying: false,
      source: null,
      syncOffset: 0,
    });
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    lastUpdateSourceRef.current = null;
    recentSyncOffsetChangeRef.current = 0;
  }, [initialTime]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Stable identity across renders: consumers (SessionAnalyticsPage) key
  // memoized callbacks off this object, and SynchronizedVideoPlayer re-runs
  // its controls-registration effect (which writes fresh state upward) when
  // those callbacks change — a fresh literal here made that cycle run on
  // every render ("Maximum update depth exceeded" render loop).
  const actions: VideoTelemetrySyncActions = useMemo(
    () => ({
      setCurrentTime,
      setSelectedVideoId,
      setIsPlaying,
      syncToTime,
      setSyncOffset,
      reset,
    }),
    [
      setCurrentTime,
      setSelectedVideoId,
      setIsPlaying,
      syncToTime,
      setSyncOffset,
      reset,
    ],
  );

  return [state, actions] as const;
}
